import { isFullBoardUrl } from './compute-full-url';
import URI from '@dw/urijs-esm';
import isEmpty from 'lodash-es/isEmpty';
import get from 'lodash-es/get';
import firestoreRedux from '@dreamworld/firestore-redux';
import { from64 } from './router-utils.js';

/**
 * This promise function is resolved with parse page data if currently accessed url is a board page otherwise reject promise.
 */
export const boardPageRouter = (data) => {
  return new Promise(async (resolve, reject) => {
    if(!isFullBoardUrl(data.path)) {
      reject(data);
      return;
    }

    const uri = new URI(data.path);
    const accountId = uri.segment()[0];
    const boardId = uri.segment()[2];
    const innerPageId = uri.segment()[3] || '';
    const cpageView = uri.segment()[1] === 'c';
    let cardId;
    let canvasRefId;
    if(innerPageId && innerPageId.startsWith('cnvs_')) {
      canvasRefId = innerPageId.split('cnvs_')[1] || '';
      canvasRefId = from64(canvasRefId);
    } else {
      if(innerPageId && innerPageId.startsWith('crd_')) {
        cardId = innerPageId;
      }
    }


    data.accountId = accountId;

    // The card dialog (top card) is always the path board + card. `_getDialogData`
    // also parses the `stack` query param into `dialog.params.stack`.
    data.dialog = cardId ? _getDialogData({ boardId, cardId }) : {};

    // The path board is the board the top card actually lives on. Resolve it
    // first — it decides whether this URL is valid at all, and it stays the
    // background whenever the stack root can't safely replace it.
    const pathPageData = await _resolvePageData({ data, accountId, boardId, cpageView, canvasRefId });

    if (!pathPageData) {
      // When board doesn't exist / no access.
      data.dialog = {};
      data.page = { name: 'NOT_FOUND' };
      resolve(data);
      return;
    }

    // The URL path points at the top (focused) card's board. When that card sits
    // on a stack, the visible BACKGROUND board is the stack ROOT's board — the
    // board the user came from — so swap the PAGE (background) board to it.
    // This works across accounts too: `data.accountId` stays the path (card's)
    // account, keeping the focused card and its actions correct, while the
    // background renders the root board from its own board-scoped data. The
    // board-move watcher is told not to re-navigate on that expected account
    // mismatch (see `subscribeAccountId` in redux/board/saga.js).
    // The source is the stack ROOT when a card is stacked behind. When the card
    // was opened from a board with no card open (e.g. a global-search jump to
    // another board), there's no stack card to derive it from, so the board the
    // user came from is carried in the `bg` query param (accountId:boardId).
    // Only swap when:
    //  - the path board isn't private, because that private view is what tells
    //    the user they've lost access to the card's own board;
    //  - the root is genuinely readable (`allowPrivate: false`) — a shared URL
    //    whose `stack`/`bg` points at a board the viewer can't open must not
    //    render a private page behind the card, which would force it to close.
    const bgSource = get(data, 'dialog.params.stack.0') || _parseBgParam(_getQueryParams().bg);
    let pageData = pathPageData;

    if (bgSource && bgSource.boardId !== boardId && !get(pathPageData, 'params.private')) {
      const rootPageData = await _resolvePageData({
        data,
        accountId: bgSource.accountId,
        boardId: bgSource.boardId,
        cpageView,
        canvasRefId,
        allowPrivate: false
      });

      if (rootPageData) {
        pageData = rootPageData;
      }
    }

    data.page = pageData;
    resolve(data);
    return;
  });
}

/**
 * Resolves the background page-data object for a given account/board.
 * Returns the page-data object, or `null` when the board doesn't exist / the
 * user has no access (caller decides the fallback / NOT_FOUND behaviour).
 * @param {Boolean} allowPrivate When `false`, a board the user can't read
 *  resolves to `null` instead of the "private board" page. Used for the stack
 *  root, where a private page must not be rendered behind an open card.
 */
const _resolvePageData = async ({ data, accountId, boardId, cpageView, canvasRefId, allowPrivate = true }) => {
  // When board exists in accessible board.
  if (get(data, `accessibleBoards.${accountId}.${boardId}`)) {
    const boardType = (data.accessibleBoards[accountId][boardId] === 'WHITEBOARD') ? 'WHITEBOARD' : 'TASKBOARD';
    return _getPageData({ boardId, type: boardType, cpageView, canvasRefId });
  }

  try {
    const query = firestoreRedux.getDocById(`boards`, boardId, { once: true });
    const doc = await query.result;
    // For public board.
    if (!isEmpty(doc)) {
      if (doc.privacy === 'PUBLIC') {
        return _getPageData({ boardId, type: doc.type, cpageView, canvasRefId });
      }
      throw new Error('permission-denied');
    }
  } catch (error) {
    // For Private board.
    const errorString = error && error.toString && error.toString() || '';
    if (allowPrivate && error && ((error.includes && error.includes('permission-denied')) || (errorString && errorString.includes('permission-denied')))) {
      return _getPageData({ boardId, cpageView, privatePage: true, canvasRefId });
    }
  }

  return null;
}

/**
 *
 * @param {*} param0
 *  @property {Object} board Board detail. e.g. {}
 * @returns
 */
const _getPageData = ({boardId, type, cpageView, canvasId, privatePage, canvasRefId}) => {
  //Board type
  let boardType = (type === 'WHITEBOARD' || type === 'RECTANGLE' || type === 'ELLIPSE' || type === 'RHOMBUS') ? 'WHITEBOARD' : 'TASKBOARD';
  let pageType = !!canvasRefId ? 'WHITEBOARD': boardType;
  let params = _getQueryParams();
  return {
    name: 'BOARD',
    params: {
      type: boardType || 'TASKBOARD',
      boardId,
      pageType,
      canvasId: (boardType === 'WHITEBOARD') ? canvasId : '',
      canvasRefId,
      private: privatePage || false,
      cpageView,
      ...params
    }
  };

};


const _getDialogData = ({ boardId, cardId }) => {
  if (!cardId) {
    return {};
  }

  //Board card detail dialog
  const params = _getQueryParams();
  let tab = 'description';
  const queryTabParam = new URI().query(true).tab;
  if (queryTabParam && queryTabParam.match("^(description|tasks|comments|attachments|history)$")) {
    tab = queryTabParam;
  }
  params.tab = tab;
  params.stack = _parseStackParam(params.stack);

  return {
    name: 'CARD',
    params: {
      boardId,
      cardId,
      ...params
    }
  }
}

const VALID_CARD_TAB_REGEX = /^(description|tasks|comments|attachments|history)$/;
const VALID_AICHAT_REGEX = /^(new|aic_[A-Za-z0-9_-]+)$/;

/**
 * Parse the `stack` query parameter into an ordered array of card entries.
 * Format: "accountId:boardId:cardId:tab[:aichat[:history]],..."
 * Entries without accountId/boardId/cardId are skipped.
 */
const _parseStackParam = (stackParam) => {
  if (!stackParam || typeof stackParam !== 'string') {
    return [];
  }
  return stackParam.split(',').reduce((entries, raw) => {
    const [accountId, boardId, cardId, rawTab, rawAichat, rawHistory] = raw.split(':');
    if (!accountId || !boardId || !cardId) {
      return entries;
    }
    const tab = rawTab && VALID_CARD_TAB_REGEX.test(rawTab) ? rawTab : 'description';
    const aichat = rawAichat && VALID_AICHAT_REGEX.test(rawAichat) ? rawAichat : null;
    const showingHistory = !!aichat && rawHistory === '1';
    entries.push({ accountId, boardId, cardId, tab, aichat, showingHistory });
    return entries;
  }, []);
}

/**
 * Parse the `bg` (background board) query param into `{ accountId, boardId }`.
 * Format: "accountId:boardId". Returns `null` when absent or malformed.
 * Used to keep the board a search jump was launched from as the background,
 * when there's no stacked card to derive it from.
 */
const _parseBgParam = (bgParam) => {
  if (!bgParam || typeof bgParam !== 'string') {
    return null;
  }
  const [accountId, boardId] = bgParam.split(':');
  if (!accountId || !boardId) {
    return null;
  }
  return { accountId, boardId };
}

/**
 * @return { Object } - All query parameters hash of current url.
 */
const _getQueryParams = () => {
  return new URI().query(true);
}